import React, { useContext, useState } from "react";
import "./AccountMaintenance.css";
import { Link } from "react-router-dom";
import { INVITE_SENT_SUCCESS_MESSAGE } from "../../../utils/constants";
import useAirlineAdminApi from "../../../services/api/useAirlineAdminApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import ROUTE_PATHS from "../../../utils/routePaths";
import accountMaintenanceLogo from "../../../assets/images/setting-icon.svg";
import CommercialAirlineNavigationBar from "../../../components/element/CommercialAirlineNavigationBar";
import AccountUpdateTable from "./Tables/AccountUpdateTable";
import DeleteAccountDialog from "../../../components/element/DeleteAccountDialog";
import InviteUserDialog from "./Views/InviteUserDialog";
import EditAccountDialog from "../../../components/element/EditAccountDialog";
import SnackbarAlert from "../../../components/element/SnackbarAlert";
import { Button } from "@mui/material";
import { StorageContext } from "../../../services/StorageProvider";

const AccountMaintenance = () => {
  const {
    getAirlineAssociation,
    inviteCommercialUserProfile,
    updateAirlineAssociation,
    deleteAirlineAssociation,
  } = useAirlineAdminApi();
  const { associatedAirlineData } = useContext(StorageContext);
  const [showInviteUserDialog, setShowInviteUserDialog] = useState(false);
  const [showEditAccountDialog, setShowEditAccountDialog] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [actionCount, setActionCount] = useState(0);

  const {
    data: accountApprovalData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["get-airline-association", actionCount],
    queryFn: getAirlineAssociation,
    retry: false,
  });

  const inviteCommercialAirlineUserMutation = useMutation({
    mutationFn: ({ userData }) => inviteCommercialUserProfile(userData),
    onSuccess: () => {
      setShowInviteUserDialog(false);
      setActionCount(actionCount + 1);
    },
  });

  const updateCommercialProfileMutation = useMutation({
    mutationFn: ({ data }) => updateAirlineAssociation(data),
    onSuccess: () => {
      setShowEditAccountDialog(false);
      setActionCount(actionCount + 1);
    },
  });

  const deleteCommercialProfileMutation = useMutation({
    mutationFn: ({ associationId }) => deleteAirlineAssociation(associationId),
    onSuccess: () => {
      setShowDeleteAccountDialog(false);
      setActionCount(actionCount + 1);
    },
  });

  const handleAccountUpdateEdit = (item) => {
    setSelectedProfile(item);
    setShowEditAccountDialog(true);
  };

  const handleAccountUpdateDelete = (item) => {
    setSelectedProfile(item);
    setShowDeleteAccountDialog(true);
  };

  const handleInviteCommercialAirlineUser = (
    firstName,
    lastName,
    email,
    selectedAccountType,
    selectedAirlineAccess,
    selectedAirports
  ) => {
    inviteCommercialAirlineUserMutation.mutate({
      userData: {
        firstName: firstName,
        lastName: lastName,
        email: email,
        role: selectedAccountType,
        airlineId: selectedAirlineAccess,
        airports: selectedAirports,
      },
    });
  };

  const handleEditAccountSave = (
    profileId,
    selectedAirports,
    selectedAccountType,
    selectedAirlineAccess
  ) => {
    updateCommercialProfileMutation.mutate({
      data: {
        profileId: profileId,
        role: selectedAccountType,
        airports: selectedAirports,
        airlineId: selectedAirlineAccess,
      },
    });
  };

  const handleAccountUpdateConfirmDelete = () => {
    deleteCommercialProfileMutation.mutate({
      associationId: selectedProfile?.associationId,
    });
  };

  return (
    <div>
      {inviteCommercialAirlineUserMutation.isSuccess ? (
        <SnackbarAlert type={"success"}>
          {INVITE_SENT_SUCCESS_MESSAGE}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {inviteCommercialAirlineUserMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {inviteCommercialAirlineUserMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {deleteCommercialProfileMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {deleteCommercialProfileMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {updateCommercialProfileMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {updateCommercialProfileMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <div>
        <CommercialAirlineNavigationBar
          title={"Account Maintenance"}
          fullWidthContent={true}
          navigationLogo={accountMaintenanceLogo}
          backContent={
            <div className="navigation-header">
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
                className="navigation-header-link"
              >
                Home/
              </Link>
              Account Maintenance
            </div>
          }
        />
        <div className="account-maintenance-header-container">
          <Button
            variant="contained"
            color="send"
            className="invite"
            onClick={() => setShowInviteUserDialog(true)}
          >
            Invite
          </Button>
        </div>
        <AccountUpdateTable
          accountUpdateData={accountApprovalData?.data}
          isLoading={isLoading}
          error={error}
          onClickDelete={handleAccountUpdateDelete}
          onClickEdit={handleAccountUpdateEdit}
        />
      </div>
      <InviteUserDialog
        open={showInviteUserDialog}
        onClose={() => setShowInviteUserDialog(false)}
        onClickAdd={handleInviteCommercialAirlineUser}
        isLoading={inviteCommercialAirlineUserMutation?.isPending}
        airlineData={associatedAirlineData}
      />
      <EditAccountDialog
        open={showEditAccountDialog}
        selectedProfile={selectedProfile}
        onClose={() => setShowEditAccountDialog(false)}
        onClickSave={handleEditAccountSave}
        isLoading={updateCommercialProfileMutation?.isPending}
        airlineData={associatedAirlineData}
      />
      <DeleteAccountDialog
        open={showDeleteAccountDialog}
        username={`${selectedProfile?.firstName} ${selectedProfile?.lastName}`}
        onClose={() => setShowDeleteAccountDialog(false)}
        onClickDelete={handleAccountUpdateConfirmDelete}
        isLoading={deleteCommercialProfileMutation?.isPending}
      />
    </div>
  );
};

export default AccountMaintenance;
